import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/sonner";
import { fmtIST } from "@/lib/utils";

const STATUS_META = {
  pending_teacher: { label: "Pending with Teacher", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_hod: { label: "Pending with HOD", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

const COLUMNS = [
  ["created_at", "Applied On"],
  ["student_name", "Student"],
  ["date", "Class Date"],
  ["semester_name", "Semester"],
  ["period_label", "Period"],
  ["subject_name", "Subject"],
  ["teacher_name", "Teacher"],
  ["status", "Status"],
];

function SortHead({ label, k, sortKey, sortDir, onSort }) {
  return (
    <TableHead>
      <button type="button" onClick={() => onSort(k)} data-testid={`sort-${k}`}
        className="inline-flex items-center gap-1 hover:text-slate-950 transition-colors">
        {label}
        {sortKey === k && (sortDir === "asc"
          ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </button>
    </TableHead>
  );
}

const latestReason = (a) =>
  a.admin_decision?.reason || a.hod_decision?.reason || a.teacher_decision?.reason || "—";

const uniq = (list) => [...new Set(list.filter(Boolean))].sort();

export default function AppealsAdmin() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({ status: "all", semester: "all", subject: "all", teacher: "all", search: "" });
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [decision, setDecision] = useState(null); // {appeal, action}
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: appeals = [] } = useQuery({
    queryKey: ["admin-appeals"],
    queryFn: async () => (await api.get("/appeals/admin")).data,
  });

  const options = useMemo(() => ({
    semesters: uniq(appeals.map((a) => a.semester_name)),
    subjects: uniq(appeals.map((a) => a.subject_name)),
    teachers: uniq(appeals.map((a) => a.teacher_name)),
  }), [appeals]);

  const rows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const filtered = appeals.filter((a) =>
      (filters.status === "all" || a.status === filters.status) &&
      (filters.semester === "all" || a.semester_name === filters.semester) &&
      (filters.subject === "all" || a.subject_name === filters.subject) &&
      (filters.teacher === "all" || a.teacher_name === filters.teacher) &&
      (!q || a.student_name.toLowerCase().includes(q) || (a.roll_number || "").toLowerCase().includes(q)));
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((x, y) => String(x[sortKey] ?? "").localeCompare(String(y[sortKey] ?? "")) * dir);
  }, [appeals, filters, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir("asc");
    }
  };

  const openDecide = (appeal, action) => {
    setReason("");
    setDecision({ appeal, action });
  };

  const submitDecision = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/appeals/admin/${decision.appeal.id}/decide`, { action: decision.action, reason });
      toast.success(decision.action === "approve" ? "Appeal force-approved" : "Appeal force-rejected");
      setDecision(null);
      queryClient.invalidateQueries({ queryKey: ["admin-appeals"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Attendance Appeals">
      <div className="bg-white border border-slate-200 rounded-md" data-testid="admin-appeals-card">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="font-heading text-lg font-semibold tracking-tight text-slate-950">All appeals</h2>
          <p className="text-xs text-slate-500 mt-1">
            Full appeal trail across departments. Admin decisions are final and override the normal workflow.
          </p>
        </div>

        <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap gap-3" data-testid="appeal-filters">
          <div className="w-64">
            <Input placeholder="Search student name or roll no" data-testid="appeal-filter-search"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="w-48" data-testid="appeal-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="appeal-filter-status-all">All statuses</SelectItem>
              <SelectItem value="pending_teacher" data-testid="appeal-filter-status-pending-teacher">Pending with Teacher</SelectItem>
              <SelectItem value="pending_hod" data-testid="appeal-filter-status-pending-hod">Pending with HOD</SelectItem>
              <SelectItem value="approved" data-testid="appeal-filter-status-approved">Approved</SelectItem>
              <SelectItem value="rejected" data-testid="appeal-filter-status-rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.semester} onValueChange={(v) => setFilters({ ...filters, semester: v })}>
            <SelectTrigger className="w-44" data-testid="appeal-filter-semester"><SelectValue placeholder="Semester" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="appeal-filter-semester-all">All semesters</SelectItem>
              {options.semesters.map((s) => (
                <SelectItem key={s} value={s} data-testid={`appeal-filter-semester-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.subject} onValueChange={(v) => setFilters({ ...filters, subject: v })}>
            <SelectTrigger className="w-48" data-testid="appeal-filter-subject"><SelectValue placeholder="Subject" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="appeal-filter-subject-all">All subjects</SelectItem>
              {options.subjects.map((s) => (
                <SelectItem key={s} value={s} data-testid={`appeal-filter-subject-${s}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.teacher} onValueChange={(v) => setFilters({ ...filters, teacher: v })}>
            <SelectTrigger className="w-44" data-testid="appeal-filter-teacher"><SelectValue placeholder="Teacher" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="appeal-filter-teacher-all">All teachers</SelectItem>
              {options.teachers.map((t) => (
                <SelectItem key={t} value={t} data-testid={`appeal-filter-teacher-${t}`}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              {COLUMNS.map(([k, label]) => (
                <SortHead key={k} k={k} label={label} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              ))}
              <TableHead>Student's Reason</TableHead>
              <TableHead>Decision Reason</TableHead>
              <TableHead className="w-44">Admin Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-slate-400 py-10" data-testid="admin-appeals-empty">
                  No appeals match the current filters.
                </TableCell>
              </TableRow>
            )}
            {rows.map((a) => {
              const meta = STATUS_META[a.status] || STATUS_META.pending_teacher;
              const active = a.status === "pending_teacher" || a.status === "pending_hod";
              return (
                <TableRow key={a.id} data-testid={`admin-appeal-row-${a.id}`}>
                  <TableCell className="font-data text-sm whitespace-nowrap">{fmtIST(a.created_at)}</TableCell>
                  <TableCell>
                    <p className="font-medium">{a.student_name}</p>
                    <p className="text-xs text-slate-400 font-data">{a.roll_number}</p>
                  </TableCell>
                  <TableCell className="font-data whitespace-nowrap">{a.date}</TableCell>
                  <TableCell>{a.semester_name}</TableCell>
                  <TableCell>{a.period_label}</TableCell>
                  <TableCell>{a.subject_name}</TableCell>
                  <TableCell className="text-slate-500">{a.teacher_name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={meta.cls} data-testid={`admin-appeal-status-${a.id}`}>
                      {meta.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-48">{a.reason}</TableCell>
                  <TableCell className="text-sm text-slate-500 max-w-48">{latestReason(a)}</TableCell>
                  <TableCell>
                    {active ? (
                      <div className="flex gap-2">
                        <Button size="sm" data-testid={`admin-approve-btn-${a.id}`}
                          onClick={() => openDecide(a, "approve")}
                          className="bg-emerald-700 hover:bg-emerald-800 text-white">
                          <Check className="h-4 w-4" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`admin-reject-btn-${a.id}`}
                          onClick={() => openDecide(a, "reject")}
                          className="text-red-700 border-red-300 hover:bg-red-50">
                          <X className="h-4 w-4" /> Reject
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Decided</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!decision} onOpenChange={() => setDecision(null)}>
        <DialogContent className="bg-white rounded-md" data-testid="admin-appeal-decide-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize">
              Force {decision?.action} — {decision?.appeal.student_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500 font-data">
              {decision?.appeal.date} · {decision?.appeal.period_label} · {decision?.appeal.subject_name} · {decision?.appeal.teacher_name}
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
              <p className="text-xs text-slate-400 mb-1">Student's reason</p>
              <p className="text-sm text-slate-800">{decision?.appeal.reason}</p>
            </div>
            {decision?.action === "approve" && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                Approving will mark this student present for this class, skipping the remaining workflow.
              </p>
            )}
            <div className="space-y-1.5">
              <p className="text-sm text-slate-500">Your reason (required)</p>
              <Textarea rows={3} data-testid="admin-appeal-decide-reason-input"
                placeholder="Why are you overriding this appeal?"
                value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <Button onClick={submitDecision} disabled={saving} data-testid="admin-appeal-decide-submit-btn"
              className={`w-full text-white ${decision?.action === "approve"
                ? "bg-emerald-700 hover:bg-emerald-800" : "bg-red-700 hover:bg-red-800"}`}>
              {saving ? "Saving…" : `Confirm ${decision?.action}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
